import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Bank, Check, CurrencyDollar, Hourglass, HandCoins } from "@phosphor-icons/react";

const METHODS = [
  { k: "bank", label: "Virement bancaire (RIB / IBAN)", placeholder: "IBAN: FR76 XXXX...\nBIC: XXXXXXXX" },
  { k: "paypal", label: "PayPal", placeholder: "email@paypal.com" },
  { k: "wise", label: "Wise", placeholder: "@wiseuser ou email Wise" },
  { k: "other", label: "Autre", placeholder: "Instructions de paiement" },
];

export default function PayoutsTab({ user, refresh }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    payout_method: user?.payout_info?.payout_method || "bank",
    payout_details: user?.payout_info?.payout_details || "",
    payout_holder: user?.payout_info?.payout_holder || "",
    payout_notes: user?.payout_info?.payout_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const fetchData = async () => {
    try {
      const { data } = await api.get("/artists/me/payouts");
      setData(data);
    } catch (err) { toast.error(formatErr(err)); }
  };

  useEffect(() => { fetchData(); }, []);

  const requestPayout = async () => {
    const balance = data?.balance_due || 0;
    if (balance <= 0) return;
    const hasInfo = user?.payout_info?.payout_details && user?.payout_info?.payout_holder;
    if (!hasInfo) {
      toast.error("Enregistre d'abord tes coordonnées de paiement à droite.");
      return;
    }
    if (!window.confirm(`Demander le paiement de $${balance.toFixed(2)} ?\n\nL'admin sera notifié et te versera le montant sur les coordonnées enregistrées.`)) return;
    setRequesting(true);
    try {
      await api.post("/artists/me/request-payout");
      toast.success("Demande envoyée ! L'admin a été notifié.");
      fetchData();
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setRequesting(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/artists/me/payout-info", form);
      toast.success("Coordonnées enregistrées");
      refresh && refresh();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const method = METHODS.find((m) => m.k === form.payout_method) || METHODS[0];

  return (
    <div className="grid lg:grid-cols-[1fr_1fr] gap-6" data-testid="payouts-tab">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-[#FF3B00] p-6 bg-[#0A0A0A]">
            <div className="text-xs uppercase tracking-widest text-white/40">Solde à recevoir</div>
            <div className="font-display text-5xl mt-2 text-[#FF3B00]">${data?.balance_due?.toFixed(2) ?? "—"}</div>
            <div className="text-xs text-white/50 mt-1">Après commission plateforme (15%)</div>
          </div>
          <div className="border border-white/10 p-6 bg-[#0A0A0A]">
            <div className="text-xs uppercase tracking-widest text-white/40">Total encaissé</div>
            <div className="font-display text-5xl mt-2">${data?.total_earned?.toFixed(2) ?? "—"}</div>
            <div className="text-xs text-white/50 mt-1">Depuis le début</div>
          </div>
        </div>

        {/* Payout request card */}
        {data?.pending_request ? (
          <div className="border border-yellow-500/50 p-6 bg-yellow-500/5" data-testid="payout-request-pending">
            <div className="flex items-center gap-2 text-yellow-400 text-xs uppercase tracking-widest mb-2">
              <Hourglass size={14} weight="bold" />
              Demande en cours
            </div>
            <div className="font-display text-3xl">${data.pending_request.amount?.toFixed(2)}</div>
            <p className="text-sm text-white/60 mt-2">
              Envoyée le {new Date(data.pending_request.requested_at).toLocaleDateString("fr-FR")}. L'admin traitera votre paiement sous peu.
            </p>
          </div>
        ) : (data?.balance_due > 0) ? (
          <button
            onClick={requestPayout}
            disabled={requesting}
            data-testid="request-payout-btn"
            className="w-full py-5 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500] disabled:opacity-50 inline-flex items-center justify-center gap-3 text-lg font-bold"
          >
            <HandCoins size={22} weight="bold" />
            {requesting ? "Envoi..." : "Je veux être payé"}
          </button>
        ) : (
          <div className="border border-white/10 p-6 bg-[#0A0A0A] text-center text-white/40 text-sm">
            Aucun solde à réclamer. Continuez à vendre !
          </div>
        )}

        <div className="border border-white/10 p-6 bg-[#0A0A0A]">
          <div className="text-xs uppercase tracking-widest text-white/40 mb-4 flex items-center gap-2"><Bank size={14} /> Historique des versements</div>
          {!data?.history?.length ? (
            <div className="text-white/50 text-sm py-4">Aucun versement pour le moment.</div>
          ) : (
            <div className="space-y-2">
              {data.history.map((h) => (
                <div key={h.id} className="flex items-center justify-between py-3 border-b border-white/5">
                  <div>
                    <div className="font-display text-2xl">${h.amount.toFixed(2)}</div>
                    <div className="text-xs text-white/50 mt-1">
                      {new Date(h.paid_at).toLocaleDateString("fr-FR")} · {h.order_count} commande(s) · {h.method || "—"}
                      {h.reference && ` · Réf: ${h.reference}`}
                    </div>
                    {h.note && <div className="text-xs text-white/40 mt-1">{h.note}</div>}
                  </div>
                  <div className="text-xs uppercase px-2 py-1 bg-green-500/20 text-green-400 flex items-center gap-1"><Check size={12} /> Payé</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <form onSubmit={save} className="border border-white/10 p-6 bg-[#0A0A0A] h-fit" data-testid="payout-info-form">
        <div className="text-xs uppercase tracking-widest text-white/40 mb-4 flex items-center gap-2"><CurrencyDollar size={14} /> Coordonnées de paiement</div>
        <p className="text-xs text-white/50 mb-4">
          Ces informations sont utilisées par l'admin pour vous virer manuellement vos gains hors plateforme.
        </p>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60">Méthode</label>
            <select value={form.payout_method} onChange={upd("payout_method")} data-testid="payout-method"
              className="w-full mt-1 bg-black border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3">
              {METHODS.map((m) => <option key={m.k} value={m.k}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60">Nom du titulaire</label>
            <input value={form.payout_holder} onChange={upd("payout_holder")} data-testid="payout-holder"
              placeholder="Prénom NOM"
              className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60">Détails</label>
            <textarea rows={4} value={form.payout_details} onChange={upd("payout_details")} data-testid="payout-details"
              placeholder={method.placeholder}
              className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3 font-mono text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/60">Notes (optionnel)</label>
            <input value={form.payout_notes} onChange={upd("payout_notes")}
              placeholder="Ex: paiements mensuels préférés"
              className="w-full mt-1 bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-4 py-3" />
          </div>
          <button type="submit" disabled={saving} data-testid="payout-save"
            className="w-full py-3 bg-[#FF3B00] text-white uppercase tracking-widest hover:bg-[#E03500] disabled:opacity-50">
            {saving ? "..." : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}
